/**
 * MVP-472 follow-up 2. Which karaoke room THIS SCREEN is driving.
 *
 * Deliberately per-browser, not per-account, and stored beside the guest's device id for exactly
 * that reason. A shop has one KJ login and a machine in each room: if the room lived on the
 * account, one operator signing in at the machine in room 5 would silently repoint the screen in
 * room 3, and there would be nothing on either screen saying why. The account answers "which shop",
 * which is the question that genuinely belongs to a person; the browser answers "which room",
 * because that is a fact about where the machine is standing.
 *
 * It follows that the ops console and the Stage window opened from it agree for free — same origin,
 * same localStorage, same room — while a second machine down the corridor keeps its own pick.
 *
 * The value is the room's `slug`, not its numeric id: slugs are what the server matches on
 * (`roomIdForOperator` in strapi-template/src/utils/venue-scope.js) and they survive a database
 * restore that renumbers rows. Nothing here is trusted by the server — it re-reads the room and
 * refuses any slug outside the operator's own venue.
 *
 * Same localStorage-with-in-memory-fallback shape as `lib/deviceId.ts` and `api/client.ts`'s token
 * storage, and the same small deliberate duplication: those modules keep their helpers private.
 */

const ROOM_SLUG_KEY = 'mvcp.roomSlug';

// localStorage exists in every real browser but not under `node --test` / vitest's node
// environment. The in-memory fallback keeps this module correct AND testable with no mocking.
const memoryStore = new Map<string, string>();

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/**
 * The room slug this browser last picked, or null when it has never picked one — which is every
 * existing install, and means "whatever the server considers this venue's default room". That null
 * is the reason this whole feature is additive: a screen nobody has configured behaves exactly as
 * it did before the picker existed.
 */
export function getRoomSlug(): string | null {
  if (hasLocalStorage()) {
    try {
      return localStorage.getItem(ROOM_SLUG_KEY) || null;
    } catch {
      /* private-mode/storage-disabled browsers: fall through to memory */
    }
  }
  return memoryStore.get(ROOM_SLUG_KEY) ?? null;
}

/** Picking a room. Pass `null` to go back to the venue's default room. */
export function setRoomSlug(slug: string | null): void {
  const trimmed = slug?.trim() ?? '';
  if (!trimmed) {
    if (hasLocalStorage()) {
      try {
        localStorage.removeItem(ROOM_SLUG_KEY);
      } catch {
        /* fall through to memory */
      }
    }
    memoryStore.delete(ROOM_SLUG_KEY);
    return;
  }
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(ROOM_SLUG_KEY, trimmed);
      return;
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.set(ROOM_SLUG_KEY, trimmed);
}
